/**
 * Content Moderation Utility
 * Checks for vulgar and unacceptable words in user-generated content
 */

const VULGAR_WORDS = [
  'fuck',
  'shit',
  'bitch',
  'asshole',
  'bastard',
  'damn',
  'crap',
  'piss',
  'dick',
  'cock',
  'pussy',
  'cunt',
  'whore',
  'slut',
  'nigger',
  'nigga',
  'fag',
  'faggot',
  'retard',
  'retarded',
  'rape',
  'kill yourself',
  'kys',
  // Add more as needed
];

/**
 * Checks if text contains vulgar or unacceptable words
 * @param {string} text - The text to check
 * @returns {object} - { isClean: boolean, flaggedWords: string[] }
 */
export const moderateContent = (text) => {
  if (!text || typeof text !== 'string') {
    return { isClean: true, flaggedWords: [] };
  }

  const lowerText = text.toLowerCase();
  const flaggedWords = [];

  // Check for exact matches and word boundaries
  VULGAR_WORDS.forEach((word) => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(lowerText)) {
      flaggedWords.push(word);
    }
  });

  // Check for leetspeak variations (e.g., "f*ck", "sh!t")
  const leetspeakPattern = /[f][*u@][c][k]|[s][h!][i1][t]|[b][i1!][t][c][h]/gi;
  if (leetspeakPattern.test(lowerText)) {
    flaggedWords.push('leetspeak variation');
  }

  // Check for excessive caps (potential spam/yelling)
  const capsRatio = (text.match(/[A-Z]/g) || []).length / text.length;
  if (capsRatio > 0.7 && text.length > 20) {
    flaggedWords.push('excessive caps');
  }

  // Check for suspicious patterns (repeated characters, spam)
  const repeatedCharsPattern = /(.)\1{5,}/;
  if (repeatedCharsPattern.test(text)) {
    flaggedWords.push('spam pattern');
  }

  return {
    isClean: flaggedWords.length === 0,
    flaggedWords: flaggedWords,
  };
};

/**
 * Sanitizes user input to prevent XSS and other injection attacks
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeInput = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  // Remove HTML tags
  let sanitized = text.replace(/<[^>]*>/g, '');

  // Remove script tags and content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Trim whitespace
  sanitized = sanitized.trim();

  return sanitized;
};
